import * as _ from 'lodash'
import {
    IBusinessObject,
    IBusinessObjectChanges,
    IBusinessObjectChangesOptions,
    IBusinessObjectCreateLoadOptions
} from '../PA.Core/PA.Core.IF.BO'
import { IMessageProvider, IProgressProvider, MessageType } from '../PA.Core/PA.Core.IF.Basic'
import { PAdbContext } from '../PA.Core/PAdbContext'
import { IdentityProvider } from '../PA.Core/Basic/Identity'
import { MessageProvider } from '../PA.Core/Basic/Messages'
import { ProgressProvider } from '../PA.Core/Basic/Progress'
import {
    WB_CompanyRow,
    WB_EventTypeRow,
    WB_MasterDataSetRow,
    WB_PlanRow,
    WB_SalesAreaRow,
    WB_SalesOrgRow,
    WB_Snapshot_ParamAMRRow,
    WB_Snapshot_ParamETRRow,
    WB_Snapshot_ParamGLCHRRow,
    WB_Snapshot_ParamGLELRRow,
    WB_Snapshot_ParamGLPHRRow,
    WB_Snapshot_ParamGLRRow,
    WB_Snapshot_ParamPHRRow,
    WB_Snapshot_ParamPlnRRow,
    WB_Snapshot_ParamRow,
    WB_Snapshot_ParamSARRow,
    WB_WorkflowStatusRow
} from '../PA.Core/PAdbSchemaRows'
import { IwbSnapshotParam } from '../PA.Core/PAdbSchemaRowsIF'
import {
    esWB_MasterData_Set,
    esWB_Snapshot_Param,
    esWB_Snapshot_ParamGLR,
    esWB_Snapshot_ParamSAR,
    esWB_Snapshot_ParamAMR,
    esWB_SalesArea,
    esWB_SalesOrg,
    esWB_Company,
    esWB_Snapshot_ParamGLELR,
    esWB_General_Ledger_Account,
    esWB_Snapshot_ParamGLCHR,
    esWB_Snapshot_ParamGLPHR,
    esWB_Snapshot_ParamPHR,
    esWB_Workflow_Status,
    esWB_Snapshot_ParamCHR,
    esWB_Plan,
    esWB_Snapshot_ParamPlnR,
    esWB_Snapshot_ParamETR,
    esWB_Event_Type,
    esWB_Event
} from '../PA.Core/PAdbSchema'
import { ESDataStore } from '../PA.Core/lib/ESDataStore'
import { ESRecord } from '../PA.Core/lib/ESRecord'
import { CreateOptions, ESRecordResolver } from '../PA.Core/lib/ESRecordResolver'
import { ASync } from '../PA.Core/Basic/ASync'
import { PAEnvironment } from '../PA.Core/PAEnvironment'
import { DefaultChanges } from '../PA.Core/BusinessObject/BusinessObjectChanges'
import { SnapshotParameterBOValidations } from './SnapshotParameterBO.Validations'
import { IJobParam } from '../PA.Job.Core/PA.Job.IF.JobParam'
import { JobParamManager } from '../PA.Job.Core/JobParamManager'
import { BehaviorSubject } from 'rxjs'
import * as PAdbSchemaRows from '../PA.Core/PAdbSchemaRows'
import * as Rx from 'rxjs'
import { IHaveEventTypes, IHavePlans, IHaveSalesAreas } from '../PA.Core/PA.Core.IF.Aspects'
import { ExecutionSerializer } from '../PA.Core/Basic/ExecutionSerializer'
import { SnapshotService } from '../PA.Snapshot.Services/SnapshotService'
import { IAccrualSnapshotScopeOptions } from '../PA.Snapshot.Core/PA.Snapshot.IF.Aspects'
import { JSONUtils } from '../PA.Core/Basic/JSON'
import { RecordUtils } from '../PA.Core/Basic/Records'

export interface ISnapshotParameterLoadOptions {
    dbContext?: PAdbContext
}

const SnapshotParameterLoadOptions_default: ISnapshotParameterLoadOptions = {}

export class SnapshotParameterBO implements IBusinessObject, IHaveSalesAreas, IHavePlans, IHaveEventTypes {
    //<editor-fold desc = "Properties and Constructor">

    Identity: IdentityProvider

    CreateOptions: any

    LoadOptions: any
    _bIsLoaded: boolean
    _OnLoadComplete: Promise<IBusinessObject>
    LoadProgressProvider: IProgressProvider

    ValidateOptions: any
    _bIsValidated: boolean
    ValidateMessageProvider: IMessageProvider
    ValidateProgressProvider: IProgressProvider

    SaveOptions: any
    _bIsSaved: boolean
    SaveProgressProvider: IProgressProvider

    DeleteOptions: any
    _bIsDeleted: boolean
    DeleteMessageProvider: IMessageProvider
    DeleteProgressProvider: IProgressProvider

    _expenseAccountsChanged: BehaviorSubject<SnapshotParameterBO>

    dbContext: PAdbContext
    drWB_SnapshotParameter: WB_Snapshot_ParamRow

    constructor(options?: IBusinessObjectCreateLoadOptions) {
        this.CreateOptions = options || {}

        if ('dbContext' in this.CreateOptions) this.dbContext = this.CreateOptions.dbContext

        this.Identity = new IdentityProvider({ id: 0, name: 'New Snapshot Parameter' })

        this.LoadOptions = {}
        this._bIsLoaded = false
        this._OnLoadComplete = undefined
        this.LoadProgressProvider = new ProgressProvider('Loading', this)

        this.ValidateOptions = {}
        this._bIsValidated = false
        this.ValidateMessageProvider = new MessageProvider()
        this.ValidateProgressProvider = new ProgressProvider('Validating', this)

        this.SaveOptions = {}
        this._bIsSaved = false
        this.SaveProgressProvider = new ProgressProvider('Saving', this)

        this.DeleteOptions = {}
        this._bIsDeleted = false
        this.DeleteMessageProvider = new MessageProvider()
        this.DeleteProgressProvider = new ProgressProvider('Deleting', this)
        this._expenseAccountsChanged = new BehaviorSubject<SnapshotParameterBO>(this)
        this.drWB_SnapshotParameter = undefined

        this.IHaveSalesArea_Init()
        this.IHavePlan_Init()
        this.IHaveEventType_Init()
        this.IHaveHierarchies_Init()
    }

    //</editor-fold>

    get ExpenseAccountsChanged(): BehaviorSubject<SnapshotParameterBO> {
        return this._expenseAccountsChanged
    }

    get WB_SnapshotParameter(): WB_Snapshot_ParamRow {
        return this.drWB_SnapshotParameter
    }

    //<editor-fold desc = "IBusinessObject operations">

    async LoadNew(options?: IBusinessObjectCreateLoadOptions): Promise<IBusinessObject> {
        const self = this
        this.LoadProgressProvider.start('Creating Snapshot Parameter')

        let internalOnComplete = (self._OnLoadComplete = (async () => {
            try {
                self.LoadOptions = options || {}
                if ('dbContext' in self.LoadOptions) self.dbContext = self.LoadOptions.dbContext
                else self.dbContext = new PAdbContext()

                if (this.dbContext == null) {
                    const sError = 'Unable to determine dbContext for new Snapshot Parameter.'
                    self.LoadProgressProvider.exception(sError)
                    return self
                }

                // Create record using dbContext
                let newSnapshotParameterRow: WB_Snapshot_ParamRow = <WB_Snapshot_ParamRow>(
                    await self.dbContext.createRecordWithKey(esWB_Snapshot_Param)
                )
                newSnapshotParameterRow.setDefaults()
                self.drWB_SnapshotParameter = newSnapshotParameterRow
                self.dbContext.add(esWB_Snapshot_Param, newSnapshotParameterRow)
                self.Identity.setAll(
                    self.WB_SnapshotParameter.cif.WB_SNAPSHOT_PARAM_ID,
                    self.WB_SnapshotParameter.cif.PARAM_ID,
                    self.WB_SnapshotParameter.cif.PARAM_NAME
                )
                self.Identity.enableRecordWriteBack(self.drWB_SnapshotParameter, 'PARAM_ID', 'PARAM_NAME')
                self.LoadProgressProvider.setName('Loading ' + self.Identity.name + '(' + self.Identity.id_ui + ')')

                self.LoadProgressProvider.finish()
                self._bIsLoaded = true
                self._OnLoadComplete = null
                await self.applyCreateOptions()
                await self.IJobParam_OnLoadComplete()
                await self.createDefaultRows()
                await self.OnIHaveSalesAreasLoaded
                // await self.OnIHavePlansLoaded;
                // await self.OnIHaveEventTypesLoaded;
                await self.HierarchiesAfterLoadComplete()

                return self
            } catch (err) {
                self.LoadProgressProvider.exception(err)
                return self
            }
        })())

        return internalOnComplete
    }

    async applyCreateOptions() {
        const self = this

        let sId = self.CreateOptions?.sId || self.LoadOptions?.sId
        if (!_.isEmpty(sId)) self.WB_SnapshotParameter.cif.PARAM_ID = sId

        let sName = self.CreateOptions?.sName || self.LoadOptions?.sName
        if (!_.isEmpty(sName)) self.WB_SnapshotParameter.cif.PARAM_NAME = sName

        // Set Master Data Set
        let wbMasterDataSet = self.CreateOptions?.wbMasterDataSetId || self.LoadOptions?.wbMasterDataSetId
        if (_.isNumber(wbMasterDataSet)) {
            let mds: WB_MasterDataSetRow = <any>await self.dbContext.find(esWB_MasterData_Set, wbMasterDataSet)
            if (mds) {
                self.WB_SnapshotParameter.WB_MasterDataSetRow = mds
                self.WB_SnapshotParameter.cif.WB_MDSET_ID = wbMasterDataSet
            }
        }

        // Set Sales Org
        let wbSalesOrg = self.CreateOptions?.wbSalesOrgId || self.LoadOptions?.wbSalesOrgId
        if (_.isNumber(wbSalesOrg)) {
            let salesOrg: WB_SalesOrgRow = <any>await self.dbContext.find(esWB_SalesOrg, wbSalesOrg)
            if (salesOrg) {
                self.WB_SnapshotParameter.WB_SalesOrgRow = salesOrg
                self.WB_SnapshotParameter.cif.WB_SALESORG_ID = wbSalesOrg
            }
        }

        // Set Company
        let wbCompany = self.CreateOptions?.wbCompanyId || self.LoadOptions?.wbCompanyId
        if (_.isNumber(wbCompany)) {
            let company: WB_CompanyRow = <any>await self.dbContext.find(esWB_Company, wbCompany)
            if (company) {
                self.WB_SnapshotParameter.WB_CompanyRow = company
                self.WB_SnapshotParameter.cif.WB_COMPANY_ID = wbCompany
            }
        }

        let sType = self.CreateOptions?.sType || self.LoadOptions?.sType
        if (!_.isEmpty(sType)) self.WB_SnapshotParameter.cif.PARAM_TYPE = sType
    }

    _glrDefaultRow: boolean
    _amrDefaultRow: boolean

    async createDefaultRows() {
        const self = this

        if (self.WB_SnapshotParameter.cif.PARAM_TYPE == 'SVF' && self.WB_SnapshotParameter.isActiveNew) {
            self.WB_SnapshotParameter.cif.ALL_PLAN_IND = '1'
            self.WB_SnapshotParameter.cif.ALL_SALESAREA_IND = '1'
        }

        if (self.WB_SnapshotParameter.cif.PARAM_TYPE == 'Event' && self.WB_SnapshotParameter.isActiveNew) {
            self.WB_SnapshotParameter.cif.ALL_EVENT_IND = '1'
            self.WB_SnapshotParameter.cif.ALL_PLAN_IND = '1'
            self.WB_SnapshotParameter.cif.ALL_SALESAREA_IND = '1'
        }

        let checkExisting = _.filter(
            self.WB_SnapshotParameter.WB_Snapshot_ParamGLRRows,
            (aRow) => aRow.cif.WB_EVTSTYPE_ID == 0
        )
        if (checkExisting.length > 0) return

        let defaultGLRow: WB_Snapshot_ParamGLRRow = <any>(
            await self.dbContext.createRecordWithKey(esWB_Snapshot_ParamGLR)
        )

        defaultGLRow.setDefaults()
        defaultGLRow.cif.WB_SNAPSHOT_PARAM_ID = self.drWB_SnapshotParameter.cif.WB_SNAPSHOT_PARAM_ID
        defaultGLRow.cif.WB_EVTSTYPE_ID = 0
        defaultGLRow.cif.EVALUATION_ORDER = 1
        defaultGLRow.cif.WB_FUNDTYPE_ID = 0
        defaultGLRow.cif.PAYMENT_METHOD = 'All'
        defaultGLRow.cif.GL_EXPENSETYPE_ID = 'All'
        await self.dbContext.add(esWB_Snapshot_ParamGLR, defaultGLRow)
        this._glrDefaultRow = true

        //Default AMR row
        let checkExistingAMR = _.filter(
            self.WB_SnapshotParameter.WB_Snapshot_ParamAMRRows,
            (aRow) => aRow.cif.WB_EVTSTYPE_ID == 0
        )
        if (checkExistingAMR.length > 0) return

        let defaultAMRRow: WB_Snapshot_ParamAMRRow = <any>(
            await self.dbContext.createRecordWithKey(esWB_Snapshot_ParamAMR)
        )

        defaultAMRRow.setDefaults()
        defaultAMRRow.cif.WB_SNAPSHOT_PARAM_ID = self.drWB_SnapshotParameter.cif.WB_SNAPSHOT_PARAM_ID
        defaultAMRRow.cif.WB_EVTSTYPE_ID = 0
        defaultAMRRow.cif.EVALUATION_ORDER = 1
        defaultAMRRow.cif.RATE_BASIS_ID = 'All'
        await self.dbContext.add(esWB_Snapshot_ParamAMR, defaultAMRRow)
        this._amrDefaultRow = true
    }

    /**
     * For each sales area under the snapshot's sales org, create a SAR record if not existing
     */
    async createParamSARRows() {
        const self = this
        const dctSARRowBySalesAreaId = _.keyBy(
            self.WB_SnapshotParameter.WB_Snapshot_ParamSARRows,
            (row) => row.cif.WB_SALESAREA_ID
        )

        for (const salesAreaRow of self._allSalesAreas) {
            const salesAreaId = salesAreaRow.cif.WB_SALESAREA_ID
            if (!(salesAreaId in dctSARRowBySalesAreaId)) {
                await self.addSalesArea(salesAreaId)
            }
        }
    }

    async createParamPlnRRows(planList: WB_PlanRow[]) {
        const self = this
        const dctPlnRRowByPlanId = _.keyBy(
            self.WB_SnapshotParameter.WB_Snapshot_ParamPlnRRows,
            (row) => row.cif.WB_PLAN_ID
        )

        for (const planRow of planList) {
            const planId = planRow.cif.WB_PLAN_ID
            if (!(planId in dctPlnRRowByPlanId)) {
                await self.addPlan(planId)
            }
        }
    }

    async createParamETRRows(eventTypeList: WB_EventTypeRow[]) {
        const self = this
        const dctETRRowByEventTypeId = _.keyBy(
            self.WB_SnapshotParameter.WB_Snapshot_ParamETRRows,
            (row) => row.cif.WB_EVTTYPE_ID
        )

        for (const eventTypeRow of eventTypeList) {
            const eventTypeId = eventTypeRow.cif.WB_EVTTYPE_ID
            if (!(eventTypeId in dctETRRowByEventTypeId)) {
                await self.addEventType(eventTypeId)
            }
        }
    }

    Load(anId: number | number, options?: object): Promise<IBusinessObject> {
        const self = this

        let recordResolver: ESRecordResolver
        let recordResolverLoadPromise: Promise<void>

        self.LoadProgressProvider.start('Loading (' + anId + ')')

        self._OnLoadComplete = new Promise<IBusinessObject>((resolve, error) => {
            if (self.bIsLoaded) {
                self.LoadProgressProvider.exception('Snapshot Parameter already loaded.')
                resolve(self) // "Snapshot Parameter already loaded.";
                return
            }

            self.LoadOptions = options || {}
            _.defaults(self.LoadOptions, SnapshotParameterLoadOptions_default)

            if ('dbContext' in self.LoadOptions) self.dbContext = self.LoadOptions.dbContext
            else if (_.isUndefined(self.dbContext)) {
                self.dbContext = new PAdbContext({})
            }

            if (_.isUndefined(self.dbContext)) {
                error('Unable to determine dbContext for Snapshot Parameter.')
                return
            }

            if ('RecordResolver' in self.LoadOptions) {
                recordResolver = (<any>self.LoadOptions).RecordResolver
                recordResolverLoadPromise = ASync.resolve()
            } else {
                recordResolver = SnapshotParameterBO.createRecordResolver({ dbContext: self.dbContext })
                recordResolverLoadPromise = SnapshotParameterBO.setupRecordResolver(recordResolver, [anId])
            }

            recordResolverLoadPromise
                .then(function () {
                    let aSnapshotParameterRow: any = recordResolver.get(esWB_Snapshot_Param, anId)

                    if (_.isEmpty(aSnapshotParameterRow)) {
                        error('Snapshot Parameter not Found')
                        return
                    }

                    self.drWB_SnapshotParameter = aSnapshotParameterRow
                    self.Identity.setAll(
                        self.WB_SnapshotParameter.cif.WB_SNAPSHOT_PARAM_ID,
                        self.WB_SnapshotParameter.cif.PARAM_ID,
                        self.WB_SnapshotParameter.cif.PARAM_NAME
                    )
                    self.LoadProgressProvider.setName('Loading ' + self.Identity.name + '(' + self.Identity.id_ui + ')')
                    self.Identity.listenToRecordUpdates(
                        self.WB_SnapshotParameter,
                        'WB_SNAPSHOT_PARAM_ID',
                        'PARAM_ID',
                        'PARAM_NAME'
                    )
                    self.Identity.enableRecordWriteBack(self.drWB_SnapshotParameter, 'PARAM_ID', 'PARAM_NAME')
                    return self.IJobParam_OnLoadComplete()
                })
                .then(() => {
                    self._bIsLoaded = true
                    self._OnLoadComplete = undefined
                    self.LoadProgressProvider.finish()
                    return self.createDefaultRows()
                })
                .then(() => {
                    return self.OnIHaveSalesAreasLoaded
                })
                .then(() => {
                    return self.OnIHavePlansLoaded
                })
                .then(() => {
                    return self.OnIHaveEventTypesLoaded
                })
                .then(() => {
                    return self.syncExpenseLiabilityAccountRel()
                })
                .then(() => {
                    return self.HierarchiesAfterLoadComplete()
                })
                .then(() => {
                    return self.syncCustomerSARel()
                })
                .then(() => {
                    return self.syncProductSARel()
                })
                .then(() => {
                    resolve(self)
                })
                .catch(function (err: any) {
                    self._OnLoadComplete = undefined
                    self.LoadProgressProvider.exception(err)
                    error(err)
                    return
                })
        })

        return this._OnLoadComplete
    }

    get OnLoadComplete(): Promise<IBusinessObject> {
        return this._OnLoadComplete
    }

    get bIsLoaded(): boolean {
        return this._bIsLoaded
    }

    async Copy(options?: any): Promise<IBusinessObject> {
        const self = this
        let src = self.WB_SnapshotParameter
        let newBO = new SnapshotParameterBO({})
        await newBO.LoadNew(
            // Create Options
            {
                sId: src.cif.PARAM_ID + '_Copy',
                sName: src.cif.PARAM_NAME + '_Copy',
                wbMasterDataSetId: src.cif.WB_MDSET_ID,
                wbCompanyId: src.cif.WB_COMPANY_ID,
                wbSalesOrgId: src.cif.WB_SALESORG_ID,
                sType: src.cif.PARAM_TYPE
            }
        )
        if (!newBO.bIsLoaded) return undefined
        let tgt = newBO.WB_SnapshotParameter

        newBO.Identity.setAll(tgt.cif.WB_SNAPSHOT_PARAM_ID, tgt.cif.PARAM_ID, tgt.cif.PARAM_NAME)

        _.forEach(tgt.WB_Snapshot_ParamGLRRows, (aRow) => {
            aRow.markDeleted()
        })

        _.forEach(tgt.WB_Snapshot_ParamAMRRows, (aRow) => {
            aRow.markDeleted()
        })

        await RecordUtils.CopyProperties(
            self.dbContext,
            esWB_Snapshot_Param,
            src,
            tgt,
            [],
            ['PARAM_ID', 'PARAM_NAME', 'CREATED_DTTS', 'MODIFIED_DTTS', 'DELETED_DT', 'DELETED_IND'],
            {}
        )

        await RecordUtils.CopyRows(
            newBO.dbContext,
            esWB_Snapshot_ParamGLR,
            _.filter(self.drWB_SnapshotParameter.WB_Snapshot_ParamGLRRows, (row) => row.isActive),
            [],
            ['WB_SNAPSHOT_PARAM_ID', 'CREATED_DTTS', 'MODIFIED_DTTS', 'DELETED_DT', 'DELETED_IND'],
            { WB_SNAPSHOT_PARAM_ID: tgt.cif.WB_SNAPSHOT_PARAM_ID }
        )

        await RecordUtils.CopyRows(
            newBO.dbContext,
            esWB_Snapshot_ParamAMR,
            _.filter(self.drWB_SnapshotParameter.WB_Snapshot_ParamAMRRows, (row) => row.isActive),
            [],
            ['WB_SNAPSHOT_PARAM_ID', 'CREATED_DTTS', 'MODIFIED_DTTS', 'DELETED_DT', 'DELETED_IND'],
            { WB_SNAPSHOT_PARAM_ID: tgt.cif.WB_SNAPSHOT_PARAM_ID }
        )

        await RecordUtils.CopyRows(
            newBO.dbContext,
            esWB_Snapshot_ParamSAR,
            _.filter(self.drWB_SnapshotParameter.WB_Snapshot_ParamSARRows, (row) => row.isActive),
            [],
            ['WB_SNAPSHOT_PARAM_ID', 'CREATED_DTTS', 'MODIFIED_DTTS', 'DELETED_DT', 'DELETED_IND'],
            { WB_SNAPSHOT_PARAM_ID: tgt.cif.WB_SNAPSHOT_PARAM_ID }
        )

        await RecordUtils.CopyRows(
            newBO.dbContext,
            esWB_Snapshot_ParamGLELR,
            _.filter(self.drWB_SnapshotParameter.WB_Snapshot_ParamGLELRRows, (row) => row.isActive),
            [],
            ['WB_SNAPSHOT_PARAM_ID', 'CREATED_DTTS', 'MODIFIED_DTTS', 'DELETED_DT', 'DELETED_IND'],
            { WB_SNAPSHOT_PARAM_ID: tgt.cif.WB_SNAPSHOT_PARAM_ID }
        )

        await RecordUtils.CopyRows(
            newBO.dbContext,
            esWB_Snapshot_ParamCHR,
            _.filter(self.drWB_SnapshotParameter.WB_Snapshot_ParamCHRRows, (row) => row.isActive),
            [],
            ['WB_SNAPSHOT_PARAM_ID', 'CREATED_DTTS', 'MODIFIED_DTTS', 'DELETED_DT', 'DELETED_IND'],
            { WB_SNAPSHOT_PARAM_ID: tgt.cif.WB_SNAPSHOT_PARAM_ID }
        )

        await RecordUtils.CopyRows(
            newBO.dbContext,
            esWB_Snapshot_ParamPHR,
            _.filter(self.drWB_SnapshotParameter.WB_Snapshot_ParamPHRRows, (row) => row.isActive),
            [],
            ['WB_SNAPSHOT_PARAM_ID', 'CREATED_DTTS', 'MODIFIED_DTTS', 'DELETED_DT', 'DELETED_IND'],
            { WB_SNAPSHOT_PARAM_ID: tgt.cif.WB_SNAPSHOT_PARAM_ID }
        )

        await RecordUtils.CopyRows(
            newBO.dbContext,
            esWB_Snapshot_ParamGLCHR,
            _.filter(self.drWB_SnapshotParameter.WB_Snapshot_ParamGLCHRRows, (row) => row.isActive),
            [],
            ['WB_SNAPSHOT_PARAM_ID', 'CREATED_DTTS', 'MODIFIED_DTTS', 'DELETED_DT', 'DELETED_IND'],
            { WB_SNAPSHOT_PARAM_ID: tgt.cif.WB_SNAPSHOT_PARAM_ID }
        )

        await RecordUtils.CopyRows(
            newBO.dbContext,
            esWB_Snapshot_ParamGLPHR,
            _.filter(self.drWB_SnapshotParameter.WB_Snapshot_ParamGLPHRRows, (row) => row.isActive),
            [],
            ['WB_SNAPSHOT_PARAM_ID', 'CREATED_DTTS', 'MODIFIED_DTTS', 'DELETED_DT', 'DELETED_IND'],
            { WB_SNAPSHOT_PARAM_ID: tgt.cif.WB_SNAPSHOT_PARAM_ID }
        )

        return newBO
    }

    async Validate(options?: any): Promise<IBusinessObject> {
        const self = this
        this.ValidateMessageProvider.clearMessages()
        this.ValidateProgressProvider.start(
            'Validating ' +
                self.WB_SnapshotParameter.cif.PARAM_NAME +
                '(' +
                self.WB_SnapshotParameter.cif.PARAM_ID +
                ')'
        )

        self.ValidateOptions = options || {}

        try {
            if (self.dbContext == undefined) {
                const sError = 'Unable to determine dbContext to validate Snapshot Parameter.'
                self.ValidateProgressProvider.exception(sError)
                throw sError
            }

            await SnapshotParameterBOValidations.ValidateFields(self)

            if (self.ValidateMessageProvider.countMessages(MessageType.Error) > 0) {
                self._bIsValidated = false
            } else {
                self._bIsValidated = true
            }
            self.ValidateProgressProvider.finish()
        } catch (err) {
            self._bIsValidated = false
            self.ValidateProgressProvider.exception(err)
        }
        return self
    }

    get bIsValidated(): boolean {
        return this._bIsValidated
    }

    Save(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(false, options)
    }

    SaveBatch(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(true, options)
    }

    async SaveUsing(bBatch: boolean, options?: { bSkipValidations?: boolean }): Promise<IBusinessObject> {
        const self = this
        const wbSnapshotParam = <IwbSnapshotParam>self.drWB_SnapshotParameter
        this.SaveProgressProvider.start(
            'Saving ' + wbSnapshotParam.PARAM_NAME + '(' + wbSnapshotParam.WB_SNAPSHOT_PARAM_ID + ')'
        )

        try {
            if (_.isEmpty(self.dbContext)) {
                self._bIsSaved = false
                const sError = 'Unable to determine dbContext to Save Snapshot Param.'
                self.SaveProgressProvider.exception(sError)
                return self
            }

            self.SaveOptions = options || {}

            let saveFunction: (
                esName: string,
                saveRecords: ESRecord[] | ESRecord,
                options?: any
            ) => Promise<ESDataStore>

            if (bBatch) {
                saveFunction = self.dbContext.SaveBatch.bind(self.dbContext)
            } else {
                saveFunction = self.dbContext.Save.bind(self.dbContext)
            }

            if (!options?.bSkipValidations) {
                await self.Validate({})

                if (!self.bIsValidated) {
                    self._bIsSaved = false
                    self.SaveProgressProvider.finish()
                    return self
                }
            }

            await saveFunction(esWB_Snapshot_Param, self.drWB_SnapshotParameter, {})
            await saveFunction(esWB_Snapshot_ParamGLR, self.drWB_SnapshotParameter.WB_Snapshot_ParamGLRRows)
            await saveFunction(esWB_Snapshot_ParamAMR, self.drWB_SnapshotParameter.WB_Snapshot_ParamAMRRows)
            await saveFunction(esWB_Snapshot_ParamSAR, self.drWB_SnapshotParameter.WB_Snapshot_ParamSARRows)
            if (self.drWB_SnapshotParameter.cif.ALL_PLAN_IND == '1') {
                await self.createParamPlnRRows(self._allPlans)
            } else {
                await self.createParamPlnRRows(self.Plans)
            }
            await saveFunction(esWB_Snapshot_ParamPlnR, self.drWB_SnapshotParameter.WB_Snapshot_ParamPlnRRows)
            if (self.drWB_SnapshotParameter.cif.ALL_EVENT_IND == '1') {
                await self.createParamETRRows(self._allEventTypes)
            } else {
                await self.createParamETRRows(self.EventTypes)
            }
            await saveFunction(esWB_Snapshot_ParamETR, self.drWB_SnapshotParameter.WB_Snapshot_ParamETRRows)
            await saveFunction(esWB_Snapshot_ParamGLELR, self.drWB_SnapshotParameter.WB_Snapshot_ParamGLELRRows)
            await saveFunction(esWB_Snapshot_ParamCHR, self.drWB_SnapshotParameter.WB_Snapshot_ParamCHRRows)
            await saveFunction(esWB_Snapshot_ParamPHR, self.drWB_SnapshotParameter.WB_Snapshot_ParamPHRRows)

            /** if the main hierarchy has "SalesArea" disabled then fill the hierarchy IDs with WB_CUSTH_ID_GL and WB_PRDH_ID_GL **/
            if (self.drWB_SnapshotParameter.cif.GLH_SALESAREA_IND !== '1') {
                _.forEach(
                    self.drWB_SnapshotParameter.WB_Snapshot_ParamGLCHRRows,
                    (aGLCHRRow) => (aGLCHRRow.cif.WB_CUSTH_ID = self.drWB_SnapshotParameter.cif.WB_CUSTH_ID_GL)
                )
                _.forEach(
                    self.drWB_SnapshotParameter.WB_Snapshot_ParamGLPHRRows,
                    (aGLPHRRow) => (aGLPHRRow.cif.WB_PRDH_ID = self.drWB_SnapshotParameter.cif.WB_PRDH_ID_GL)
                )
            }
            await saveFunction(esWB_Snapshot_ParamGLCHR, self.drWB_SnapshotParameter.WB_Snapshot_ParamGLCHRRows)
            await saveFunction(esWB_Snapshot_ParamGLPHR, self.drWB_SnapshotParameter.WB_Snapshot_ParamGLPHRRows)

            if (self.drWB_SnapshotParameter.isActive) {
                if (_.isEmpty(self.JobParam)) {
                    if (self.WB_SnapshotParameter.isActive) await self.IJobParam_OnLoadComplete()
                }

                if (self.JobParam) {
                    self.JobParam.id_ui = self.WB_SnapshotParameter.cif.PARAM_ID
                    self.JobParam.name = self.WB_SnapshotParameter.cif.PARAM_NAME
                    await self.JobParam.Save(bBatch)
                }
            }

            if (bBatch) {
                self.dbContext.addBatchCompleteListener(() => {
                    self._bIsSaved = true
                })
            } else {
                self._bIsSaved = true
            }

            self.SaveProgressProvider.finish()
        } catch (err) {
            self.SaveProgressProvider.exception(err)
        }
    }

    get bIsSaved(): boolean {
        return this._bIsSaved
    }

    //Delete
    Delete(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(true, options)
    }

    DeleteOnSave(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(false, options)
    }

    private async DeleteUsing(bSaveNow: boolean, options?: any): Promise<IBusinessObject> {
        const self = this
        const wbSnapshotParameter = <IwbSnapshotParam>self.drWB_SnapshotParameter
        this.DeleteMessageProvider.clearMessages()
        this.DeleteProgressProvider.start(
            'Deleting ' + wbSnapshotParameter.PARAM_NAME + '(' + wbSnapshotParameter.PARAM_ID + ')'
        )

        try {
            // 1. Validate if we can delete the Snapshot Parameter
            await SnapshotParameterBOValidations.ValidateDelete(self)
            if (self.DeleteMessageProvider.countMessages(MessageType.Error) > 0) {
                this.DeleteProgressProvider.finish()
                return self
            } else {
                // 2. If Valid, mark all the records for deletion.
                self.drWB_SnapshotParameter.markDeleted()

                if (!_.isEmpty(self.JobParam)) self.JobParam.Delete(bSaveNow)

                if (bSaveNow) {
                    await self.SaveUsing(false)
                }
                self._bIsDeleted = true
                self.DeleteProgressProvider.finish()
            }
        } catch (err) {
            this.DeleteProgressProvider.exception(err)
        }

        return self
    }

    get bIsDeleted(): boolean {
        return this._bIsDeleted
    }

    Changes(options?: IBusinessObjectChangesOptions): IBusinessObjectChanges {
        let opts = options || {}
        // Set any BO Specific change settings
        opts.dctIgnoreChanges = opts.dctIgnoreChanges || {}

        let amrDefaultRow = _.filter(
            this.WB_SnapshotParameter.WB_Snapshot_ParamAMRRows,
            (aRow) => aRow.cif.WB_EVTSTYPE_ID == 0
        )

        if (
            (this._amrDefaultRow || amrDefaultRow.length > 0) &&
            this.WB_SnapshotParameter.WB_Snapshot_ParamAMRRows.length == 1
        ) {
            opts.dctIgnoreChanges[esWB_Snapshot_ParamAMR] = { bNew: true, bDeletes: true }
        }
        if (this._glrDefaultRow && this.WB_SnapshotParameter.WB_Snapshot_ParamGLRRows.length == 1)
            opts.dctIgnoreChanges[esWB_Snapshot_ParamGLR] = { bNew: true, bDeletes: true }

        return DefaultChanges(this, this.drWB_SnapshotParameter, opts)
    }

    addGLAccountR(
        wbSpendTypeId?: number,
        wbFundType?: number,
        paymentMethod?: string,
        expenseType?: string,
        wbLiabilityAcctId?: number,
        wbExpenseAcctId?: number
    ): Promise<IBusinessObject> {
        const self = this

        return new Promise<IBusinessObject>((resolve, error) => {
            const activeGLRows = _.filter(
                self.WB_SnapshotParameter.WB_Snapshot_ParamGLRRows,
                (aRow) => aRow.isActive && aRow.cif.WB_EVTSTYPE_ID != 0
            )
            const defaultRow = _.first(
                _.filter(self.WB_SnapshotParameter.WB_Snapshot_ParamGLRRows, (aRow) => aRow.cif.WB_EVTSTYPE_ID == 0)
            )

            self.dbContext
                .createRecordWithKey(esWB_Snapshot_ParamGLR)
                .then((aNewRow: WB_Snapshot_ParamGLRRow) => {
                    aNewRow.setDefaults()
                    aNewRow.cif.WB_SNAPSHOT_PARAM_ID = self.drWB_SnapshotParameter.cif.WB_SNAPSHOT_PARAM_ID
                    aNewRow.cif.WB_EVTSTYPE_ID = 1

                    if (!_.isUndefined(wbSpendTypeId)) aNewRow.cif.WB_EVTSTYPE_ID = wbSpendTypeId
                    if (!_.isUndefined(wbFundType)) aNewRow.cif.WB_FUNDTYPE_ID = wbFundType
                    if (!_.isUndefined(paymentMethod)) aNewRow.cif.PAYMENT_METHOD = paymentMethod
                    if (!_.isUndefined(expenseType)) aNewRow.cif.GL_EXPENSETYPE_ID = expenseType
                    if (!_.isUndefined(wbExpenseAcctId)) aNewRow.cif.WB_GLACCT_ID_EXPENSE = wbExpenseAcctId
                    if (!_.isUndefined(wbLiabilityAcctId)) aNewRow.cif.WB_GLACCT_ID_LIABILITY = wbLiabilityAcctId

                    if (activeGLRows.length == 0) {
                        aNewRow.cif.EVALUATION_ORDER = 1

                        if (!_.isUndefined(defaultRow)) defaultRow.cif.EVALUATION_ORDER = 2
                    } else {
                        const maxSortOrder = _.maxBy(activeGLRows, (aRow) => aRow.cif.EVALUATION_ORDER).cif
                            .EVALUATION_ORDER
                        aNewRow.cif.EVALUATION_ORDER = maxSortOrder + 1

                        if (!_.isUndefined(defaultRow)) defaultRow.cif.EVALUATION_ORDER = maxSortOrder + 2
                    }

                    self.dbContext.add(esWB_Snapshot_ParamGLR, aNewRow)
                })
                .then(() => {
                    return self.syncExpenseLiabilityAccountRel()
                })
                .then(() => {
                    resolve(self)
                })
                .catch((err) => {
                    error(err)
                })
        })
    }

    deleteGLAccountR(aWbGLRId: number): Promise<IBusinessObject> {
        const self = this

        return new Promise<IBusinessObject>((resolve, error) => {
            _.forEach(self.drWB_SnapshotParameter.WB_Snapshot_ParamGLRRows, (aGLRRow: WB_Snapshot_ParamGLRRow) => {
                if (aGLRRow.isActive && aGLRRow.cif.WB_SNAPSHOT_PARAMGLR_ID == aWbGLRId) {
                    aGLRRow.markDeleted()
                }
            })

            const defaultRow = _.first(
                _.filter(self.WB_SnapshotParameter.WB_Snapshot_ParamGLRRows, (aRow) => aRow.cif.WB_EVTSTYPE_ID == 0)
            )
            const activeGLRows = _.filter(
                self.WB_SnapshotParameter.WB_Snapshot_ParamGLRRows,
                (aRow) => aRow.isActive && aRow.cif.WB_EVTSTYPE_ID != 0
            )

            let maxSortOrder = 0

            if (activeGLRows.length > 0)
                maxSortOrder = _.maxBy(activeGLRows, (aRow) => aRow.cif.EVALUATION_ORDER).cif.EVALUATION_ORDER

            if (!_.isUndefined(defaultRow)) defaultRow.cif.EVALUATION_ORDER = maxSortOrder + 1

            self.syncExpenseLiabilityAccountRel().then(() => {
                resolve(self)
            })
        })
    }

    // Allocation Method
    addAllocationMethodR(): Promise<IBusinessObject> {
        const self = this

        return new Promise<IBusinessObject>((resolve, reject) => {
            const activeAMRRows = _.filter(
                self.WB_SnapshotParameter.WB_Snapshot_ParamAMRRows,
                (aRow) => aRow.isActive && aRow.cif.WB_EVTSTYPE_ID !== 0
            )
            const defaultRow = _.first(
                _.filter(self.WB_SnapshotParameter.WB_Snapshot_ParamAMRRows, (aRow) => aRow.cif.WB_EVTSTYPE_ID === 0)
            )

            self.dbContext
                .createRecordWithKey(esWB_Snapshot_ParamAMR)
                .then((aNewRow: WB_Snapshot_ParamAMRRow) => {
                    aNewRow.setDefaults()
                    aNewRow.cif.WB_SNAPSHOT_PARAM_ID = self.WB_SnapshotParameter.cif.WB_SNAPSHOT_PARAM_ID
                    aNewRow.cif.WB_EVTSTYPE_ID = 1

                    if (activeAMRRows.length === 0) {
                        aNewRow.cif.EVALUATION_ORDER = 1

                        if (!_.isUndefined(defaultRow)) {
                            defaultRow.cif.EVALUATION_ORDER = 2
                        }
                    } else {
                        const maxSortOrder = _.maxBy(activeAMRRows, (aRow) => aRow.cif.EVALUATION_ORDER).cif
                            .EVALUATION_ORDER
                        aNewRow.cif.EVALUATION_ORDER = maxSortOrder + 1

                        if (!_.isUndefined(defaultRow)) {
                            defaultRow.cif.EVALUATION_ORDER = maxSortOrder + 2
                        }
                    }

                    self.dbContext.add(esWB_Snapshot_ParamAMR, aNewRow)
                    resolve(self)
                })
                .catch((err) => {
                    reject(err)
                })
        })
    }

    deleteAllocationMethodR(aWbAMRId: number): Promise<IBusinessObject> {
        const self = this

        return new Promise<IBusinessObject>((resolve, reject) => {
            _.forEach(self.WB_SnapshotParameter.WB_Snapshot_ParamAMRRows, (aRow: WB_Snapshot_ParamAMRRow) => {
                if (aRow.isActive && aRow.cif.WB_SNAPSHOT_PARAMAMR_ID === aWbAMRId) {
                    aRow.markDeleted()
                }
            })
            resolve(self)
        })
    }

    static createRecordResolver(boLoadOptions: any): ESRecordResolver {
        let opts = boLoadOptions || {}
        let dbContext: PAdbContext

        if ('dbContext' in opts) {
            dbContext = opts.dbContext
        } else {
            dbContext = new PAdbContext({})
        }
        const wrksp_options: CreateOptions = {
            standardConditions: {
                [esWB_Snapshot_ParamGLR]: { DELETED_IND: { neq: '1' } },
                [esWB_Snapshot_ParamSAR]: { DELETED_IND: { neq: '1' } },
                [esWB_Snapshot_ParamAMR]: { DELETED_IND: { neq: '1' } }
            },
            dctIncludeBelongsToNamedRelations: {}
        }

        const aRecResolver = new ESRecordResolver(dbContext, wrksp_options)

        return aRecResolver
    }

    static setupRecordResolver(recordResolver: ESRecordResolver, ids: string[] | number[]): Promise<void> {
        return new Promise<void>((resolve, error) => {
            recordResolver
                .findAllWithRelationships(esWB_Snapshot_Param, ids)
                .then((result) => {
                    resolve()
                })
                .catch((err) => {
                    error(err)
                })
        })
    }

    static concurrentLoadCountHint(): number {
        return 10
    }

    //<editor-fold desc = "Job Param">
    _jobParam: IJobParam

    async IJobParam_OnLoadComplete(): Promise<void> {
        const self = this

        const jpMgr = new JobParamManager()
        const sourceWbId = this.WB_SnapshotParameter.cif.WB_SNAPSHOT_PARAM_ID
        const jobParamId = this.WB_SnapshotParameter.cif.PARAM_ID
        const jobParamName = this.WB_SnapshotParameter.cif.PARAM_NAME
        const wbMasterDataSetId = this.WB_SnapshotParameter.cif.WB_MDSET_ID
        const wbSalesOrgId = this.WB_SnapshotParameter.cif.WB_SALESORG_ID

        let jobProcessorType = ''
        let appExecId = 'NA'
        let sourceType = 'NA'
        let dependentTriggerId = ''
        switch (this.WB_SnapshotParameter.cif.PARAM_TYPE) {
            case 'TradeAccrual':
                sourceType = 'SnapshotParameter'
                appExecId = 'TradeAccrual'
                jobProcessorType = 'AccrualSnapshotCalcQueuePopulator'
                dependentTriggerId = 'AccrualSnapshot'
                break
            case 'SVF':
                sourceType = 'SVFSnapshotParameter'
                appExecId = 'SVFSnapshotParameter'
                jobProcessorType = 'SVFSnapshotProcessor'
                dependentTriggerId = 'SVFSnapshot'
                break
            case 'Event':
                sourceType = 'EventSnapshotParameter'
                appExecId = 'EventSnapshotParameter'
                jobProcessorType = 'EventSnapshotProcessor'
                dependentTriggerId = 'EventSnapshot'
                break
        }
        if (jobProcessorType === '') {
            console.warn(`Snapshot parameter got an invalid PARAM_TYPE = ${this.WB_SnapshotParameter.cif.PARAM_TYPE}`)
        }

        this._jobParam = await jpMgr.findOrCreate({
            dbContext: this.dbContext,
            JobSchedulerModuleId: appExecId,

            sourceType: sourceType,
            sourceWbId: sourceWbId,

            appExecId: appExecId,
            wbTargetId: sourceWbId,

            wbMasterDataSetId: wbMasterDataSetId,
            wbCompanyId: self.WB_SnapshotParameter?.WB_SalesOrgRow?.cif?.WB_COMPANY_ID || 0,
            wbSalesOrgId: wbSalesOrgId,

            JobParamId: jobParamId,
            JobParamName: jobParamName,
            JobProcessorType: jobProcessorType,
            DependentTriggerId: dependentTriggerId
        })
    }

    get JobParam(): IJobParam {
        return this._jobParam
    }

    //<editor-fold>

    //<editor-fold desc="Expense-Liability account relationship">

    async syncExpenseLiabilityAccountRel(): Promise<void> {
        const self = this

        let arrGLRows = _.filter(
            self.WB_SnapshotParameter.WB_Snapshot_ParamGLRRows,
            (aGLRow: WB_Snapshot_ParamGLRRow) => aGLRow.isActive
        )

        let dctExpenseAcctIds = _.keyBy(arrGLRows, (a: WB_Snapshot_ParamGLRRow) => a.cif.WB_GLACCT_ID_EXPENSE)
        let dctExpenseAcctIds_Missing = _.clone(dctExpenseAcctIds)
        let dctExpenseAcctIds_Existing: { [wbExpenseAcctId: number]: number } = {}

        _.forEach(self.WB_SnapshotParameter.WB_Snapshot_ParamGLELRRows, (aRow: WB_Snapshot_ParamGLELRRow) => {
            if (!aRow.isActive) return

            let wbExpenseAcctId = _.toNumber(aRow.cif.WB_GLACCT_ID_EXPENSE)

            // Remove Dups
            if (wbExpenseAcctId in dctExpenseAcctIds_Existing) {
                aRow.markDeleted()
                return
            }

            // Track Existing
            dctExpenseAcctIds_Existing[wbExpenseAcctId] = wbExpenseAcctId

            // Check for Extra
            if (!(wbExpenseAcctId in dctExpenseAcctIds)) {
                aRow.markDeleted()
                return
            }

            delete dctExpenseAcctIds_Missing[wbExpenseAcctId]
        })

        // Add Missing
        for (let key in dctExpenseAcctIds_Missing) {
            await self.addGLExpenseLiabilityRelRecord(_.toNumber(key))
        }

        self.ExpenseAccountsChanged.next(self)
    }

    async addGLExpenseLiabilityRelRecord(wbExpenseAcctId: number): Promise<WB_Snapshot_ParamGLELRRow> {
        const self = this

        let expenseRow = await self.dbContext.find(esWB_General_Ledger_Account, wbExpenseAcctId)
        if (_.isUndefined(expenseRow) || _.isNull(expenseRow) || wbExpenseAcctId == 0) return

        return new Promise<WB_Snapshot_ParamGLELRRow>((resolve, error) => {
            self.dbContext
                .createRecordWithKey(esWB_Snapshot_ParamGLELR)
                .then((aNewRow: WB_Snapshot_ParamGLELRRow) => {
                    aNewRow.setDefaults()
                    aNewRow.cif.WB_GLACCT_ID_EXPENSE = wbExpenseAcctId
                    aNewRow.cif.WB_SNAPSHOT_PARAM_ID = self.WB_SnapshotParameter.cif.WB_SNAPSHOT_PARAM_ID

                    self.dbContext.add(esWB_Snapshot_ParamGLELR, aNewRow)
                    resolve(aNewRow)
                })
                .catch((err) => {
                    error(err)
                })
        })
    }

    //<editor-fold>

    //<editor-fold  desc=IHaveSalesArea>
    private _bIHaveSalesAreaLoaded: boolean
    private _salesAreas: WB_SalesAreaRow[]
    private _allSalesAreas: WB_SalesAreaRow[]
    private _salesAreasChanged: BehaviorSubject<IHaveSalesAreas>
    private _OnIHaveSalesAreasLoaded: Promise<IHaveSalesAreas>

    IHaveSalesArea_Init() {
        this._bIHaveSalesAreaLoaded = false
        this._salesAreas = []
        this._allSalesAreas = []
        this._salesAreasChanged = new BehaviorSubject<IHaveSalesAreas>(this)
        this._OnIHaveSalesAreasLoaded = undefined
    }

    get bIHaveSalesAreaLoaded(): boolean {
        return this._bIHaveSalesAreaLoaded
    }

    get OnIHaveSalesAreasLoaded(): Promise<IHaveSalesAreas> {
        const self = this

        if (self.bIHaveSalesAreaLoaded) {
            return ASync.resolve(self)
        }

        if (!_.isEmpty(self._OnIHaveSalesAreasLoaded)) return self.OnIHaveSalesAreasLoaded

        let OnLoadPromise = new Promise<IHaveSalesAreas>((resolve, error) => {
            self.dbContext
                .findAll(esWB_SalesArea, {
                    where: {
                        DELETED_IND: { neq: '1' },
                        WB_SALESORG_ID: self.drWB_SnapshotParameter.cif.WB_SALESORG_ID
                    }
                })
                .then((arrSalesArea) => {
                    self._allSalesAreas = arrSalesArea
                    if (
                        self.drWB_SnapshotParameter.cif.ALL_SALESAREA_IND == '1' &&
                        _.filter(self.drWB_SnapshotParameter.WB_Snapshot_ParamSARRows, (row) => row.isActive).length <
                            self._allSalesAreas.length
                    )
                        return self.createParamSARRows()
                })
                .then(() => {
                    self._bIHaveSalesAreaLoaded = true
                    self._OnIHaveSalesAreasLoaded = undefined

                    resolve(self)
                })
                .catch((err) => {
                    error(err)
                })
        })

        self._OnIHaveSalesAreasLoaded = OnLoadPromise
        return OnLoadPromise
    }

    get SalesAreasReadOnly(): boolean {
        return false
    }

    addSalesArea(SalesAreaId: number): Promise<void> {
        let self = this
        let row = _.first(
            _.filter(
                self.WB_SnapshotParameter.WB_Snapshot_ParamSARRows,
                (row) => row.isActive && row.cif.WB_SALESAREA_ID == SalesAreaId
            )
        )
        if (!_.isEmpty(row)) return
        ;(async () => {
            let newRow: WB_Snapshot_ParamSARRow = <any>await self.dbContext.createRecordWithKey(esWB_Snapshot_ParamSAR)
            newRow.setDefaults()
            newRow.cif.WB_SNAPSHOT_PARAM_ID = self.drWB_SnapshotParameter.cif.WB_SNAPSHOT_PARAM_ID
            newRow.WB_SnapshotParamRow = self.drWB_SnapshotParameter
            newRow.cif.WB_SALESAREA_ID = SalesAreaId
            newRow.WB_SalesAreaRow = _.first(
                _.filter(self._allSalesAreas, (sa) => sa.cif.WB_SALESAREA_ID == SalesAreaId)
            )
            self.dbContext.add(esWB_Snapshot_ParamSAR, newRow)
        })()
        return
    }

    removeSalesArea(SalesAreaId: number): Promise<void> {
        let self = this
        let row = _.first(
            _.filter(
                self.WB_SnapshotParameter.WB_Snapshot_ParamSARRows,
                (row) => row.isActive && row.cif.WB_SALESAREA_ID == SalesAreaId
            )
        )
        row.markDeleted()
        return
    }

    hasSalesArea(SalesAreaId: number): boolean {
        const self = this
        if (
            _.filter(
                self.WB_SnapshotParameter.WB_Snapshot_ParamSARRows,
                (row) => row.isActive && row.cif.WB_SALESAREA_ID == SalesAreaId
            ).length
        )
            return true
        else return false
    }

    isSalesAreaEditable(SalesAreaId: number): boolean {
        return true
    }

    get SalesAreas(): PAdbSchemaRows.WB_SalesAreaRow[] {
        const self = this
        let retVal: WB_SalesAreaRow[] = _.map(
            _.filter(self.WB_SnapshotParameter.WB_Snapshot_ParamSARRows, (row) => row.isActive),
            (row) => {
                return row.WB_SalesAreaRow
            }
        )
        return retVal
    }

    addSalesAreas(arrSalesAreaId: number[]): Promise<void> {
        let self = this
        _.forEach(this.WB_SnapshotParameter.WB_Snapshot_ParamSARRows, (row) => {
            if (_.includes(arrSalesAreaId, row.cif.WB_SALESAREA_ID)) {
                let ind = arrSalesAreaId.indexOf(row.cif.WB_SALESAREA_ID)
                arrSalesAreaId.splice(ind, 1)
            }
        })
        ;(async () => {
            for (let id of arrSalesAreaId) {
                await self.addSalesArea(id)
            }
        })()
        return
    }

    removeSalesAreas(arrSalesAreaId: number[]): Promise<void> {
        _.forEach(this.WB_SnapshotParameter.WB_Snapshot_ParamSARRows, (row) => {
            if (row.isActive && _.includes(arrSalesAreaId, row.cif.WB_SALESAREA_ID)) {
                row.markDeleted()
            }
        })
        return
    }

    get SalesAreasChanged(): Rx.BehaviorSubject<IHaveSalesAreas> {
        return this._salesAreasChanged
    }

    get AllSalesAreas(): PAdbSchemaRows.WB_SalesAreaRow[] {
        return this._allSalesAreas
    }

    //<editor-fold>

    //<editor-fold  desc=IHavePlans>
    private _bIHavePlanLoaded: boolean
    private _plans: WB_PlanRow[]
    private _allPlans: WB_PlanRow[]
    private _plansChanged: BehaviorSubject<IHavePlans>
    private _OnIHavePlansLoaded: Promise<IHavePlans>

    IHavePlan_Init() {
        this._bIHavePlanLoaded = false
        this._plans = []
        this._allPlans = []
        this._plansChanged = new BehaviorSubject<IHavePlans>(this)
        this._OnIHavePlansLoaded = undefined
    }

    get bIHavePlanLoaded(): boolean {
        return this._bIHavePlanLoaded
    }

    get OnIHavePlansLoaded(): Promise<IHavePlans> {
        const self = this

        if (self.bIHavePlanLoaded) {
            return ASync.resolve(self)
        }

        if (!_.isEmpty(self._OnIHavePlansLoaded)) return self.OnIHavePlansLoaded

        let OnLoadPromise = new Promise<IHavePlans>((resolve, error) => {
            self.dbContext
                .findAll(esWB_Plan, {
                    where: {
                        DELETED_IND: { neq: '1' },
                        WB_PERIOD_ID: self.drWB_SnapshotParameter.cif.WB_PERIOD_ID_PLANNING_YEAR,
                        WB_MDSET_ID: self.WB_SnapshotParameter.cif.WB_MDSET_ID,
                        WB_SALESORG_ID: self.WB_SnapshotParameter.cif.WB_SALESORG_ID
                    }
                })
                .then((arrPlan) => {
                    self._allPlans = arrPlan
                    self._bIHavePlanLoaded = true
                    self._OnIHavePlansLoaded = undefined
                    resolve(self)
                })
                .catch((err) => {
                    error(err)
                })
        })

        self._OnIHavePlansLoaded = OnLoadPromise
        return OnLoadPromise
    }

    get PlansReadOnly(): boolean {
        return false
    }

    async addPlan(PlanId: number): Promise<void> {
        let self = this
        let row = _.first(
            _.filter(
                self.WB_SnapshotParameter.WB_Snapshot_ParamPlnRRows,
                (row) => row.isActive && row.cif.WB_PLAN_ID == PlanId
            )
        )
        if (!_.isEmpty(row)) return

        let newRow: WB_Snapshot_ParamPlnRRow = <any>await self.dbContext.createRecordWithKey(esWB_Snapshot_ParamPlnR)
        newRow.setDefaults()
        newRow.cif.WB_SNAPSHOT_PARAM_ID = self.drWB_SnapshotParameter.cif.WB_SNAPSHOT_PARAM_ID
        newRow.WB_SnapshotParamRow = self.drWB_SnapshotParameter
        newRow.cif.WB_PLAN_ID = PlanId
        newRow.WB_PlanRow = _.first(_.filter(self._allPlans, (sa) => sa.cif.WB_PLAN_ID == PlanId))
        self.dbContext.add(esWB_Snapshot_ParamPlnR, newRow)
        return
    }

    async removePlan(PlanId: number): Promise<void> {
        let self = this
        let row = _.first(
            _.filter(
                self.WB_SnapshotParameter.WB_Snapshot_ParamPlnRRows,
                (row) => row.isActive && row.cif.WB_PLAN_ID == PlanId
            )
        )
        if (!_.isEmpty(row)) row.markDeleted()
        return
    }

    async resetPlans(): Promise<void> {
        let self = this
        self._bIHavePlanLoaded = false
        self._plans = []
        self._allPlans = []
        self.WB_SnapshotParameter.cif.ALL_PLAN_IND = '1'
        return
    }

    hasPlan(PlanId: number): boolean {
        const self = this
        if (
            _.filter(
                self.WB_SnapshotParameter.WB_Snapshot_ParamPlnRRows,
                (row) => row.isActive && row.cif.WB_PLAN_ID == PlanId
            ).length
        )
            return true
        else return false
    }

    isPlanEditable(PlanId: number): boolean {
        return true
    }

    get Plans(): PAdbSchemaRows.WB_PlanRow[] {
        const self = this
        let retVal: WB_PlanRow[] = _.map(
            _.filter(self.WB_SnapshotParameter.WB_Snapshot_ParamPlnRRows, (row) => row.isActive),
            (row) => {
                return row.WB_PlanRow
            }
        )
        return retVal
    }

    addPlans(arrPlanId: number[]): Promise<void> {
        let self = this
        _.forEach(this.WB_SnapshotParameter.WB_Snapshot_ParamPlnRRows, (row) => {
            if (_.includes(arrPlanId, row.cif.WB_PLAN_ID) && row.isActive) {
                let ind = arrPlanId.indexOf(row.cif.WB_PLAN_ID)
                arrPlanId.splice(ind, 1)
            }
        })
        ;(async () => {
            for (let id of arrPlanId) {
                await self.addPlan(id)
            }
        })()
        return
    }

    removePlans(arrPlanId: number[]): Promise<void> {
        _.forEach(this.WB_SnapshotParameter.WB_Snapshot_ParamPlnRRows, (row) => {
            if (row.isActive && _.includes(arrPlanId, row.cif.WB_PLAN_ID)) {
                row.markDeleted()
            }
        })
        return
    }

    get PlansChanged(): Rx.BehaviorSubject<IHavePlans> {
        return this._plansChanged
    }

    get AllPlans(): PAdbSchemaRows.WB_PlanRow[] {
        return this._allPlans
    }

    //<editor-fold>

    //<editor-fold  desc=IHaveEventTypes>
    private _bIHaveEventTypeLoaded: boolean
    private _eventTypes: WB_EventTypeRow[]
    private _allEventTypes: WB_EventTypeRow[]
    private _eventTypesChanged: BehaviorSubject<IHaveEventTypes>
    private _OnIHaveEventTypesLoaded: Promise<IHaveEventTypes>

    IHaveEventType_Init() {
        this._bIHaveEventTypeLoaded = false
        this._eventTypes = []
        this._allEventTypes = []
        this._eventTypesChanged = new BehaviorSubject<IHaveEventTypes>(this)
        this._OnIHaveEventTypesLoaded = undefined
    }

    get bIHaveEventTypeLoaded(): boolean {
        return this._bIHaveEventTypeLoaded
    }

    get OnIHaveEventTypesLoaded(): Promise<IHaveEventTypes> {
        const self = this

        if (self.bIHaveEventTypeLoaded) {
            return ASync.resolve(self)
        }

        if (!_.isEmpty(self._OnIHaveEventTypesLoaded)) return self.OnIHaveEventTypesLoaded

        let OnLoadPromise = (async () => {
            try {
                let plansList = self.WB_SnapshotParameter.cif.ALL_PLAN_IND == '1' ? self.AllPlans : self.Plans
                let planIds = _.map(plansList, 'WB_PLAN_ID')
                if (!_.isEmpty(planIds)) {
                    let eventsList = await self.dbContext.findAll(esWB_Event, {
                        where: {
                            DELETED_IND: { neq: '1' },
                            WB_PLAN_ID: { inq: planIds }
                        }
                    })
                    let events = _.map(eventsList, 'EVENT_TYPE_ID')
                    if (!_.isEmpty(events)) {
                        let arrEventTypes = await self.dbContext.findAll(esWB_Event_Type, {
                            where: {
                                DELETED_IND: { neq: '1' },
                                WB_EVTTYPE_ID: { inq: _.uniq(events) },
                                WB_MDSET_ID: self.WB_SnapshotParameter.cif.WB_MDSET_ID,
                                WB_SALESORG_ID: self.WB_SnapshotParameter.cif.WB_SALESORG_ID
                            }
                        })

                        self._allEventTypes = arrEventTypes
                    }
                }
                self._bIHaveEventTypeLoaded = true
                self._OnIHaveEventTypesLoaded = undefined
            } catch (err) {
                self.LoadProgressProvider.exception(err)
                return self
            }
        })()

        self._OnIHaveEventTypesLoaded = OnLoadPromise
        return OnLoadPromise
    }

    get EventTypesReadOnly(): boolean {
        return false
    }

    async addEventType(EventTypeId: number): Promise<void> {
        let self = this
        let row = _.first(
            _.filter(
                self.WB_SnapshotParameter.WB_Snapshot_ParamETRRows,
                (row) => row.isActive && row.cif.WB_EVTTYPE_ID == EventTypeId
            )
        )
        if (!_.isEmpty(row)) return

        let newRow: WB_Snapshot_ParamETRRow = <any>await self.dbContext.createRecordWithKey(esWB_Snapshot_ParamETR)
        newRow.setDefaults()
        newRow.cif.WB_SNAPSHOT_PARAM_ID = self.drWB_SnapshotParameter.cif.WB_SNAPSHOT_PARAM_ID
        newRow.WB_SnapshotParamRow = self.drWB_SnapshotParameter
        newRow.cif.WB_EVTTYPE_ID = EventTypeId
        newRow.WB_EventTypeRow = _.first(_.filter(self._allEventTypes, (sa) => sa.cif.WB_EVTTYPE_ID == EventTypeId))
        self.dbContext.add(esWB_Snapshot_ParamETR, newRow)
        return
    }

    async removeEventType(EventTypeId: number): Promise<void> {
        let self = this
        let row = _.first(
            _.filter(
                self.WB_SnapshotParameter.WB_Snapshot_ParamETRRows,
                (row) => row.isActive && row.cif.WB_EVTTYPE_ID == EventTypeId
            )
        )
        if (!_.isEmpty(row)) row.markDeleted()
        return
    }

    async resetEventTypes(): Promise<void> {
        let self = this
        self._bIHaveEventTypeLoaded = false
        self._eventTypes = []
        self._allEventTypes = []
        self.WB_SnapshotParameter.cif.ALL_EVENT_IND = '1'
        return
    }

    hasEventType(EventTypeId: number): boolean {
        const self = this
        if (
            _.filter(
                self.WB_SnapshotParameter.WB_Snapshot_ParamETRRows,
                (row) => row.isActive && row.cif.WB_EVTTYPE_ID == EventTypeId
            ).length
        )
            return true
        else return false
    }

    isEventTypeEditable(EventTypeId: number): boolean {
        return true
    }

    get EventTypes(): PAdbSchemaRows.WB_EventTypeRow[] {
        const self = this
        let retVal: WB_EventTypeRow[] = _.map(
            _.filter(self.WB_SnapshotParameter.WB_Snapshot_ParamETRRows, (row) => row.isActive),
            (row) => {
                return row.WB_EventTypeRow
            }
        )
        return retVal
    }

    addEventTypes(arrEventTypeId: number[]): Promise<void> {
        let self = this
        _.forEach(this.WB_SnapshotParameter.WB_Snapshot_ParamETRRows, (row) => {
            if (_.includes(arrEventTypeId, row.cif.WB_EVTTYPE_ID) && row.isActive) {
                let ind = arrEventTypeId.indexOf(row.cif.WB_EVTTYPE_ID)
                arrEventTypeId.splice(ind, 1)
            }
        })
        ;(async () => {
            for (let id of arrEventTypeId) {
                await self.addEventType(id)
            }
        })()
        return
    }

    removeEventTypes(arrEventTypeIds: number[]): Promise<void> {
        _.forEach(this.WB_SnapshotParameter.WB_Snapshot_ParamETRRows, (row) => {
            if (row.isActive && _.includes(arrEventTypeIds, row.cif.WB_EVTTYPE_ID)) {
                row.markDeleted()
            }
        })
        return
    }

    get EventTypesChanged(): Rx.BehaviorSubject<IHaveEventTypes> {
        return this._eventTypesChanged
    }

    get AllEventTypes(): PAdbSchemaRows.WB_EventTypeRow[] {
        return this._allEventTypes
    }

    //<editor-fold>

    //<editor-fold  desc="SnapshotServices - Scope">

    async ScopeAfterLoadComplete(): Promise<void> {
        await this.updateScopeProductLevels()
    }

    async getSnapshotScopeOptions(): Promise<IAccrualSnapshotScopeOptions> {
        const self = this
        let spr = self.WB_SnapshotParameter
        let sp = self.WB_SnapshotParameter.cif

        let allSalesAreas = self.AllSalesAreas
        let salesAreas = self.SalesAreas

        let rv: IAccrualSnapshotScopeOptions = {
            planningScope: spr.bUseDateRange
                ? { StartDt: sp.EFFECTIVE_START_DT, EndDt: sp.EFFECTIVE_END_DT }
                : +spr.cif.WB_PERIOD_ID_PLANNING_YEAR,
            wbSalesOrgId: +sp.WB_SALESORG_ID,
            wbMDSetId: self.WB_SnapshotParameter.cif.WB_MDSET_ID
        }

        if (sp.PARAM_TYPE == 'TradeAccrual') rv.bExcludeOI = spr.bExcludeOI

        if (!spr.bAllSalesAreaInd) rv.wbSalesAreaIds = _.map(salesAreas, (a) => a.cif.WB_SALESAREA_ID)

        if (sp.SELECT_TACTICS_IND == '1') {
            let arrSpendTypesIds: number[] = []
            _.forEach(sp.WB_EVTSTYPE_ID_CSV.split(','), (a) => {
                if (_.isEmpty(a) || _.isEmpty(a.trim())) return
                if (_.isNumber(+a)) arrSpendTypesIds.push(+a)
            })

            if (!_.isEmpty(arrSpendTypesIds)) rv.wbEventSpendTypeIds = arrSpendTypesIds
        }

        let eventStatusTypes = sp.EVENT_STATUS_CSV.split(',')
        let arrEvtStatusRows = <WB_WorkflowStatusRow[]>(
            await self.dbContext.findAll(esWB_Workflow_Status, { where: { STATUS_TYPE: { inq: eventStatusTypes } } })
        )
        let arrEventStatusIds: number[] = _.map(arrEvtStatusRows, (a) => a.cif.WB_STATUS_ID)

        if (!_.isEmpty(arrEventStatusIds)) rv.wbEventStatusIds = arrEventStatusIds

        let eventTacticStatusTypes = sp.TACTIC_STATUS_CSV.split(',')
        let arrEvtTacticStatusRows = <WB_WorkflowStatusRow[]>await self.dbContext.findAll(esWB_Workflow_Status, {
            where: { STATUS_TYPE: { inq: eventTacticStatusTypes } }
        })
        let arrEventTacticStatusIds: number[] = _.map(arrEvtTacticStatusRows, (a) => a.cif.WB_STATUS_ID)

        if (!_.isEmpty(arrEventTacticStatusIds)) rv.wbEventSpendStatusIds = arrEventTacticStatusIds
        rv.eventSource = sp.EVENT_SOURCE

        return rv
    }

    async updateScopeComments(): Promise<void> {
        const self = this
        let snapshotService = new SnapshotService()
        let scope = await self.getSnapshotScopeOptions()
        let counts = await snapshotService.getAccrualScope(self.WB_SnapshotParameter.cif.WB_SNAPSHOT_PARAM_ID, scope)
        this.WB_SnapshotParameter.cif.SCOPE_COMMENTS = JSON.stringify({
            PlanCount: counts.PlanCount,
            EventCount: counts.EventCount,
            EventTacticCount: counts.EventTacticCount
        })
    }

    get Scope(): any {
        return JSONUtils.parse(this.WB_SnapshotParameter.cif.SCOPE_COMMENTS, {
            PlanCount: 0,
            EventCount: 0,
            EventTacticCount: 0
        })
    }

    async updateScopeProductLevels(): Promise<void> {
        const self = this
        let snapshotService = new SnapshotService()
        let scope = await self.getSnapshotScopeOptions()
        let prdEntities = await snapshotService.getAccrualScopeProductLevels(
            self.WB_SnapshotParameter.cif.WB_SNAPSHOT_PARAM_ID,
            scope
        )
        if (
            (this.WB_SnapshotParameter.cif.WB_ENTITY_ID_PRODUCT_SNP_OPTIONS == '' ||
                this.WB_SnapshotParameter.cif.WB_ENTITY_ID_PRODUCT_SNP_OPTIONS == '') &&
            !_.isEmpty(prdEntities)
        )
            this.WB_SnapshotParameter.cif.WB_ENTITY_ID_PRODUCT_SNP_OPTIONS = prdEntities.join(',')
        // if (_.isEmpty(prdEntities))
        //     this.WB_SnapshotParameter.cif.WB_ENTITY_ID_PRODUCT_SNP_OPTIONS = "0";
    }

    //<editor-fold>

    //<editor-fold  desc=HierarchyBySalesArea>

    private _HierarchiesChanged: BehaviorSubject<SnapshotParameterBO>
    private qsSyncHierarchies: ExecutionSerializer

    IHaveHierarchies_Init() {
        const self = this
        self._HierarchiesChanged = new BehaviorSubject<SnapshotParameterBO>(self)
        self.qsSyncHierarchies = new ExecutionSerializer(self, self.syncHierarchies.bind(self), [], 1000)
    }

    async HierarchiesAfterLoadComplete(): Promise<void> {
        const self = this
        await self.OnIHaveSalesAreasLoaded

        self.SalesAreasChanged.subscribe(() => {
            self.qsSyncHierarchies.Run()
        })
    }

    get HierarchiesChanged(): BehaviorSubject<SnapshotParameterBO> {
        return this._HierarchiesChanged
    }

    SyncSalesAreaHierarchies(): void {
        if (this.qsSyncHierarchies) this.qsSyncHierarchies.Run()
    }

    private async syncHierarchies(): Promise<void> {
        const self = this

        if (!self.bIHaveSalesAreaLoaded) return

        let spr = self.WB_SnapshotParameter
        let sp = spr.cif

        let allSalesAreas = self.AllSalesAreas
        let salesAreas = self.SalesAreas
        if (_.isEmpty(salesAreas)) salesAreas = allSalesAreas

        if (self.WB_SnapshotParameter.cif.PARAM_TYPE == 'TradeAccrual') await self.updateScopeProductLevels()
        let prdEntityIds = _.map(sp.WB_ENTITY_ID_PRODUCT_SNP_OPTIONS.trim().split(','), (a) => +a)
        prdEntityIds = _.filter(prdEntityIds, (a) => a > 0)

        //-- Sync Snapshot Product Hierarchy Records
        let dctAreas = _.keyBy(salesAreas, (a) => a.cif.WB_SALESAREA_ID)
        let dctPrdEntityId = _.keyBy(prdEntityIds, (a) => a)
        let dctPHRExists: { [entityId: number]: { [wbSalesAreaId: number]: WB_Snapshot_ParamPHRRow } } = {}
        _.forEach(spr.WB_Snapshot_ParamPHRRows, (aRow) => {
            if (!aRow.isActive) return
            let entityId = aRow.cif.WB_ENTITY_ID_PRODUCT_SRC
            let saId = aRow.cif.WB_SALESAREA_ID

            if (!(entityId in dctPrdEntityId)) {
                aRow.markDeleted()
                return
            }
            if (saId != 0 && !(saId in dctAreas)) {
                aRow.markDeleted()
                return
            }

            if (!(entityId in dctPHRExists)) dctPHRExists[entityId] = {}
            if (saId in dctPHRExists[entityId]) {
                // delete duplicate
                aRow.markDeleted()
                return
            }

            dctPHRExists[entityId][saId] = aRow
        })

        // Add missing Sales Area/ Entity Type combinations
        for (let wbSalesAreaId of [0, ..._.map(salesAreas, (a) => a.cif.WB_SALESAREA_ID)]) {
            for (let wbEntityId of prdEntityIds) {
                if (!(wbEntityId in dctPHRExists)) dctPHRExists[wbEntityId] = {}

                if (wbSalesAreaId in dctPHRExists[wbEntityId]) continue

                // Create missing record
                let aNewRow = <WB_Snapshot_ParamPHRRow>await self.dbContext.createRecordWithKey(esWB_Snapshot_ParamPHR)
                aNewRow.cif.WB_SNAPSHOT_PARAM_ID = self.drWB_SnapshotParameter.cif.WB_SNAPSHOT_PARAM_ID
                aNewRow.WB_SnapshotParamRow = self.WB_SnapshotParameter
                aNewRow.cif.WB_ENTITY_ID_PRODUCT_SRC = wbEntityId
                aNewRow.cif.WB_SALESAREA_ID = wbSalesAreaId
                self.dbContext.add(esWB_Snapshot_ParamPHR, aNewRow)
                dctPHRExists[wbEntityId][wbSalesAreaId] = aNewRow
            }
        }

        self._HierarchiesChanged.next(self)
    }

    async syncCustomerSARel(): Promise<void> {
        const self = this

        let arrSARows = _.filter(
            self.WB_SnapshotParameter.WB_Snapshot_ParamSARRows,
            (aSARRow: WB_Snapshot_ParamSARRow) => aSARRow.isActive
        )

        let dctSAIds = _.keyBy(arrSARows, (a: WB_Snapshot_ParamSARRow) => a.cif.WB_SALESAREA_ID)
        let dctSAIds_Missing = _.clone(dctSAIds)
        let dctSAIds_Existing: { [wbSalesAreaId: number]: number } = {}

        _.forEach(self.WB_SnapshotParameter.WB_Snapshot_ParamGLCHRRows, (aRow: WB_Snapshot_ParamGLCHRRow) => {
            if (!aRow.isActive) return

            let wbSalesAreaId = _.toNumber(aRow.cif.WB_SALESAREA_ID)

            // Remove Dups
            if (wbSalesAreaId in dctSAIds_Existing) {
                aRow.markDeleted()
                return
            }

            // Track Existing
            dctSAIds_Existing[wbSalesAreaId] = wbSalesAreaId

            // Check for Extra
            if (!(wbSalesAreaId in dctSAIds)) {
                aRow.markDeleted()
                return
            }

            delete dctSAIds_Missing[wbSalesAreaId]
        })

        // Add Missing
        if (self.WB_SnapshotParameter.bEnableGLSalesArea)
            for (let saRRow of _.values(dctSAIds_Missing)) {
                await self.addGLCustomerHierarchySA(saRRow.cif.WB_SALESAREA_ID)
            }
    }

    async syncProductSARel(): Promise<void> {
        const self = this

        let arrSARows = _.filter(
            self.WB_SnapshotParameter.WB_Snapshot_ParamSARRows,
            (aSARRow: WB_Snapshot_ParamSARRow) => aSARRow.isActive
        )

        let dctSAIds = _.keyBy(arrSARows, (a: WB_Snapshot_ParamSARRow) => a.cif.WB_SALESAREA_ID)
        let dctSAIds_Missing = _.clone(dctSAIds)
        let dctSAIds_Existing: { [wbSalesAreaId: number]: number } = {}

        _.forEach(self.WB_SnapshotParameter.WB_Snapshot_ParamGLPHRRows, (aRow: WB_Snapshot_ParamGLPHRRow) => {
            if (!aRow.isActive) return

            let wbSalesAreaId = _.toNumber(aRow.cif.WB_SALESAREA_ID)

            // Remove Dups
            if (wbSalesAreaId in dctSAIds_Existing) {
                aRow.markDeleted()
                return
            }

            // Track Existing
            dctSAIds_Existing[wbSalesAreaId] = wbSalesAreaId

            // Check for Extra
            if (!(wbSalesAreaId in dctSAIds)) {
                aRow.markDeleted()
                return
            }

            delete dctSAIds_Missing[wbSalesAreaId]
        })

        // Add Missing
        if (self.WB_SnapshotParameter.bEnableGLSalesArea)
            for (let saRRow of _.values(dctSAIds_Missing)) {
                await self.addGLProductHierarchySA(saRRow.cif.WB_SALESAREA_ID)
            }
    }

    async addGLCustomerHierarchySA(SalesAreaId: number): Promise<void> {
        let self = this
        let row = _.first(
            _.filter(
                self.WB_SnapshotParameter.WB_Snapshot_ParamGLCHRRows,
                (row) => row.isActive && row.cif.WB_SALESAREA_ID == SalesAreaId
            )
        )
        if (!_.isEmpty(row)) return
        let newRow: WB_Snapshot_ParamGLCHRRow = <any>await self.dbContext.createRecordWithKey(esWB_Snapshot_ParamGLCHR)
        newRow.setDefaults()
        newRow.cif.WB_SNAPSHOT_PARAM_ID = self.drWB_SnapshotParameter.cif.WB_SNAPSHOT_PARAM_ID
        newRow.WB_SnapshotParamRow = self.drWB_SnapshotParameter
        newRow.cif.WB_SALESAREA_ID = SalesAreaId
        newRow.WB_SalesAreaRow = _.first(_.filter(self._allSalesAreas, (sa) => sa.cif.WB_SALESAREA_ID == SalesAreaId))
        self.dbContext.add(esWB_Snapshot_ParamGLCHR, newRow)
        return
    }

    async addGLProductHierarchySA(SalesAreaId: number): Promise<void> {
        let self = this
        let row = _.first(
            _.filter(
                self.WB_SnapshotParameter.WB_Snapshot_ParamGLPHRRows,
                (row) => row.isActive && row.cif.WB_SALESAREA_ID == SalesAreaId
            )
        )
        if (!_.isEmpty(row)) return
        let newRow: WB_Snapshot_ParamGLPHRRow = <any>await self.dbContext.createRecordWithKey(esWB_Snapshot_ParamGLPHR)
        newRow.setDefaults()
        newRow.cif.WB_SNAPSHOT_PARAM_ID = self.drWB_SnapshotParameter.cif.WB_SNAPSHOT_PARAM_ID
        newRow.WB_SnapshotParamRow = self.drWB_SnapshotParameter
        newRow.cif.WB_SALESAREA_ID = SalesAreaId
        newRow.WB_SalesAreaRow = _.first(_.filter(self._allSalesAreas, (sa) => sa.cif.WB_SALESAREA_ID == SalesAreaId))
        self.dbContext.add(esWB_Snapshot_ParamGLPHR, newRow)
        return
    }

    //<editor-fold>
}

PAEnvironment.registerBusinessObjectClass(SnapshotParameterBO, 'SnapshotParameterBO', 'Snapshot Parameter')
